import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import Box from '@material-ui/core/Box';
import List from '@material-ui/core/List';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import ArrowBackIosRounded from '@material-ui/icons/ArrowBackIosRounded';
import PersonRoundedIcon from '@material-ui/icons/PersonRounded';
import PlayCircleOutlineRoundedIcon from '@material-ui/icons/PlayCircleOutlineRounded';
import { useRoleplay } from 'features/ai-generators/hooks';
import { generateRoleplayCall } from 'features/ai-generators/services';
import { EntityRoutes } from 'routes/constants';
import { FPActionButton, When } from 'UI/components/atoms';
import { AI_ROLEPLAY_COMPANY, AI_ROLEPLAY_HIRING_AUTHORITY_NAME } from 'UI/constants/defaults';

import { PlainLink } from '../../shared';
import { AUTO_HIDE_DURATION, ROLEPLAY_DIFFICULTY_OPTIONS } from '../constants';
import { DetailPanelLayout } from '../DetailPanelLayout';
import { RoleplayCallWaiting } from '../RoleplayCallWaiting';
import { RoleplayFeedbackDrawer } from '../RoleplayFeedbackDrawer';
import {
  AIProfileAvatar,
  AIProfileListItem,
  ListItemOption,
  useRoleplayScenarioSelectorStyles
} from '../styles';

import { JobOrderRoleplayProfileFields } from './JobOrderRoleplayProfileFields';
import {
  COPIES,
  JOB_ORDERS_ROLEPLAY_SCENARIOS
} from './JobOrderRoleplayScenarioSelector.constants';
import { ArrowForwardIcon, OutlinedButtonStyled } from './JobOrderRoleplayScenarioSelector.styled';

const getDefaultValuesFromJobOrder = jobOrder => {
  const { specialty, subspecialty } = jobOrder || {};
  return {
    specialty: specialty
      ? {
          id: specialty.id,
          industry_id: specialty?.industry?.id,
          industry_title: specialty?.industry?.title,
          title: specialty?.title
        }
      : null,
    subspecialty: subspecialty || null,
    title: '',
    experience: null,
    difficulty: ROLEPLAY_DIFFICULTY_OPTIONS[0].id
  };
};

export const JobOrderRoleplayScenarioSelector = ({
  jobOrder,
  onResetJobOrderSelection,
  onSelectPresentationType = () => {},
  showJobOrderInfo = false
}) => {
  const form = useForm({
    defaultValues: getDefaultValuesFromJobOrder(jobOrder)
  });
  const { handleSubmit } = form;
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [isFeedbackDrawerOpen, setIsFeedbackDrawerOpen] = useState(false);
  const [isCallRinging, setIsCallRinging] = useState(false);
  const classes = useRoleplayScenarioSelectorStyles({ isCallRinging });
  const bottomRef = useRef(null);

  const handleScenarioSelect = ({ id, presentationMakerType, subtitle, title }) => {
    setSelectedScenario({ id, presentationMakerType, subtitle, title });
    onSelectPresentationType && onSelectPresentationType(presentationMakerType);
  };

  const {
    generatedAiData,
    handleGenerateCallWithAi,
    handleResetGeneratedData,
    uiState: uiStateRoleplay
  } = useRoleplay({
    extendedParams: { type: selectedScenario?.id || null },
    generatePresentationFn: generateRoleplayCall
  });

  const isCallDataAvailable = useMemo(
    () => Boolean(selectedScenario && generatedAiData),
    [generatedAiData, selectedScenario]
  );

  const handleNewSimulation = () => {
    handleResetGeneratedData();
    setIsFeedbackDrawerOpen(false);
  };

  const handleOnCloseFeedbackDrawer = () => {
    setIsFeedbackDrawerOpen(false);
  };

  const handleOpenFeedbackDrawer = () => {
    setIsFeedbackDrawerOpen(true);
  };

  const handleResetScenarioSelection = () => {
    setSelectedScenario(null);
    setIsCallRinging(false);
    handleNewSimulation();
  };

  useEffect(() => {
    if (isCallDataAvailable) {
      setIsCallRinging(true);
      const timerId = setTimeout(() => setIsCallRinging(false), AUTO_HIDE_DURATION);
      return () => clearTimeout(timerId);
    }

    setIsCallRinging(false);
    return undefined;
  }, [isCallDataAvailable]);

  useEffect(() => {
    if (isCallRinging) return;

    requestAnimationFrame(() => {
      if (bottomRef.current) {
        bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    });
  }, [isCallRinging]);

  const { id: jobOrderId, title: jobOrderTitle, subtitle: summary } = jobOrder || {};

  return (
    <Box className={classes.mainContainer}>
      <When condition={isCallRinging}>
        <Box height="100%">
          <RoleplayCallWaiting />
        </Box>
      </When>

      <div className={classes.content}>
        <When condition={!selectedScenario}>
          <DetailPanelLayout
            header={COPIES.selectScenario}
            body={JOB_ORDERS_ROLEPLAY_SCENARIOS.map(
              ({ id, presentationMakerType, subtitle, title }) => (
                <Box key={id}>
                  <List>
                    <ListItemOption
                      button
                      onClick={_event =>
                        handleScenarioSelect({ id, presentationMakerType, subtitle, title })
                      }
                      selected={selectedScenario?.id === id}
                    >
                      <Box flexDirection="column">
                        {title}

                        <Typography color="textSecondary" variant="body2">
                          {subtitle}
                        </Typography>
                      </Box>

                      <ListItemSecondaryAction>
                        <ArrowForwardIcon />
                      </ListItemSecondaryAction>
                    </ListItemOption>
                  </List>
                </Box>
              )
            )}
            footer={
              showJobOrderInfo && (
                <>
                  <Typography variant="subtitle1">
                    {COPIES.scenarios.callCandidate.jobOrderInfo.title}
                  </Typography>
                  <PlainLink
                    itemId={jobOrderId}
                    aria-label="Job Order Title"
                    linkProps={{
                      type: 'local',
                      url: EntityRoutes.JobOrderProfile,
                      target: '_blank'
                    }}
                    noWrap
                  >
                    {jobOrderTitle}
                  </PlainLink>
                  <Typography variant="body1">{summary}</Typography>
                  {onResetJobOrderSelection && (
                    <Box mt={3}>
                      <OutlinedButtonStyled
                        color="primary"
                        fullWidth
                        onClick={onResetJobOrderSelection}
                        startIcon={<ArrowBackIosRounded />}
                        text={COPIES.scenarios.callCandidate.jobOrderInfo.resetSelection}
                        variant="outlined"
                      />
                    </Box>
                  )}
                </>
              )
            }
          />
        </When>

        <When condition={selectedScenario}>
          <DetailPanelLayout
            header={
              <Box>
                <Typography variant="subtitle1">{selectedScenario?.title}</Typography>
                <Typography color="textSecondary" variant="body2">
                  {selectedScenario?.subtitle}
                </Typography>

                <Box mt={3}>
                  <OutlinedButtonStyled
                    color="primary"
                    fullWidth
                    onClick={handleResetScenarioSelection}
                    startIcon={<ArrowBackIosRounded />}
                    variant="outlined"
                    text={COPIES.selectDifferentScenario}
                  />
                </Box>
              </Box>
            }
            body={
              <FormContext {...form}>
                <AIProfileListItem>
                  <ListItemAvatar>
                    <AIProfileAvatar>
                      <PersonRoundedIcon />
                    </AIProfileAvatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={
                      <Typography variant="subtitle1">
                        {AI_ROLEPLAY_HIRING_AUTHORITY_NAME}
                      </Typography>
                    }
                    secondary={
                      <Typography color="textSecondary" variant="body2">
                        {AI_ROLEPLAY_COMPANY}
                      </Typography>
                    }
                  />
                </AIProfileListItem>
                <form onSubmit={handleSubmit(handleGenerateCallWithAi)}>
                  <JobOrderRoleplayProfileFields
                    disabled={isCallDataAvailable}
                    isFetching={uiStateRoleplay.isLoading}
                  />
                </form>
              </FormContext>
            }
            footer={
              isCallDataAvailable ? (
                <Box
                  alignItems="center"
                  display="flex"
                  flexDirection="column"
                  gridGap={16}
                  justifyContent="center"
                  width="100%"
                >
                  <Box display="flex" flexDirection="column" gridGap={8} mt={2} width="100%">
                    <Typography variant="subtitle2">{COPIES.feedback.info}</Typography>

                    <OutlinedButtonStyled
                      color="primary"
                      fullWidth
                      onClick={handleOpenFeedbackDrawer}
                      text={COPIES.feedback.view}
                      variant="outlined"
                    />
                  </Box>

                  <FPActionButton
                    fullWidth
                    icon={PlayCircleOutlineRoundedIcon}
                    onClick={handleNewSimulation}
                    text={COPIES.startNewSimulation}
                  />
                </Box>
              ) : null
            }
          />
        </When>

        <When condition={isFeedbackDrawerOpen && generatedAiData?.call?.id}>
          <RoleplayFeedbackDrawer
            callId={generatedAiData?.call?.id}
            onClose={handleOnCloseFeedbackDrawer}
          />
        </When>

        <div ref={bottomRef} />
      </div>
    </Box>
  );
};
